import { useFormik } from 'formik';
import Select from 'react-select';

import { DataIcons } from '@assets/icons';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@store/hooks';
import { GetCategoriesList, UpdatCategories } from '@store/INV/Categories/CategoriesSlice';
import { Form } from 'react-bootstrap';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import useGetCategoriesList from '@hooks/INV/useGetCategoriesList';
import { type CategoriesModels } from '@store/INV/Categories/CategoriesModels';
import { Card, CardHeader } from '@components/card/card';
// import useGetChartAccountsDetailsList from '@hooks/Finance/useGetChartAccountsDetailsList';
import Loading from '@components/Shared/Loader/Loading';
import { EditCategoriesINVSchema } from '../../../ValidationForm/validationSchema';
import { usePermissions } from '@Helper/Permisstions/usePermissions';

type Props = {
  categoryDetails: CategoriesModels | undefined;
};

const EditCategoryInv = ({ categoryDetails }: Props) => {
  const { HasPermission } = usePermissions();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { CategoriesList, loading: loading2, error } = useGetCategoriesList();

  const formik = useFormik({
    initialValues: {
      id: categoryDetails ? categoryDetails.id : '',
      categoryId: categoryDetails ? categoryDetails.categoryId : '',
      name2: categoryDetails ? categoryDetails.name2 : '',
      name: categoryDetails ? categoryDetails.name : '',
      description: categoryDetails ? categoryDetails.description : '',
      icon: categoryDetails ? categoryDetails.icon : '',

      revenueAccountId: categoryDetails ? categoryDetails.revenueAccountId : '',
      expenseAccountId: categoryDetails ? categoryDetails.expenseAccountId : '',
      discountAccountId: categoryDetails ? categoryDetails.discountAccountId : '',
      discountCreditAccountId: categoryDetails ? categoryDetails.discountCreditAccountId : '',
      salesReturnsAccountId: categoryDetails ? categoryDetails.salesReturnsAccountId : '',
      inventoryAccountId: categoryDetails ? categoryDetails.inventoryAccountId : '',

      image: categoryDetails ? categoryDetails.image : ''
    },

    validationSchema: EditCategoriesINVSchema(t),
    enableReinitialize: true,
    onSubmit: values => {
      dispatch(
        UpdatCategories({
          id: values.id,
          categoryId: values.categoryId,
          name: values.name,
          name2: values.name2,
          description: values.description,
          icon: values.icon,
          revenueAccountId: values.revenueAccountId,
          expenseAccountId: values.expenseAccountId,
          discountAccountId: values.discountAccountId,
          discountCreditAccountId: values.discountCreditAccountId,
          salesReturnsAccountId: values.salesReturnsAccountId,
          inventoryAccountId: values.inventoryAccountId,
          image: values.image
        })
      )
        .unwrap()
        .then(res => {
          if (res.succeeded === true) {
            dispatch(GetCategoriesList());
            CustomAlert({ action: 'Edit' });
            formik.setSubmitting(false);
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error' });
          }
        })
        .catch(error => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  return (
    <div>
      <Card className='mb-3'>
        <Loading loading={loading2} error={error}>
          <>
            <CardHeader className='d-flex justify-content-between align-items-center '>
              {/* <span className=' fw-bold'>{t('Details')}</span> */}
              <span className='fw-bold'>
                <i className='bi bi-info-circle mx-1 fa-lg text-theme'></i> {t('Details')}
              </span>

              <div className='m-1'>
                <span className='mx-0  '>
                  <i className={`${categoryDetails?.icon} text-theme`}></i>
                </span>
                <span className='m-0  '>{i18n.language === 'en' ? categoryDetails?.name : categoryDetails?.name}</span>
              </div>
            </CardHeader>

            <div className='list-group list-group-flush'>
              <div className='list-group-item d-flex px-3'>
                <div className='flex-fill'>
                  <Form onSubmit={formik.handleSubmit}>
                    <div className='row'>
                      <div className='col-xl-6'>
                        <div className='form-group mb-3'>
                          <label className='form-label'>{t('LocalName')}</label>
                          <Form.Control
                            className='form-control '
                            type='text'
                            autoComplete='off'
                            name='name2'
                            onChange={formik.handleChange}
                            value={formik.values.name2 || ''}
                            isInvalid={!!(formik.touched.name2 && formik.errors.name2)}
                          />
                          <Form.Control.Feedback type='invalid'>{formik.errors.name2}</Form.Control.Feedback>
                        </div>
                      </div>

                      <div className='col-xl-6'>
                        <div className='form-group mb-3'>
                          <label className='form-label'>{t('GlobalName')}</label>
                          <Form.Control
                            className='form-control '
                            type='text'
                            autoComplete='off'
                            name='name'
                            onChange={formik.handleChange}
                            value={formik.values.name || ''}
                            isInvalid={!!(formik.touched.name && formik.errors.name)}
                          />
                          <Form.Control.Feedback type='invalid'>{formik.errors.name}</Form.Control.Feedback>
                        </div>
                      </div>

                      <div className='col-xl-6'>
                        <div className='form-group mb-3'>
                          <label className='form-label'> {t('الفئة الأب')}</label>
                          <Form.Select
                            // disabled
                            className='form-select'
                            name='categoryId'
                            onChange={formik.handleChange}
                            value={formik.values.categoryId ?? ''}
                            isInvalid={!!(formik.touched.categoryId && formik.errors.categoryId)}
                          >
                            {/* <option value="">{i18n.language === 'ar' ? 'إختر' : 'Choose'}</option> */}
                            <option value='' selected={formik.values.categoryId === null}>
                              {'فئة رئيسية'}
                            </option>
                            {CategoriesList &&
                              CategoriesList?.map((item, idx) => (
                                <option key={++idx} value={item.id}>
                                  {i18n.language === 'en' ? item.name : item.name2}
                                </option>
                              ))}
                          </Form.Select>
                        </div>
                      </div>

                      <div className='col-lg-6'>
                        <div className='form-group mb-3'>
                          <label className='form-label'>{t('Icon')}</label>
                          <Select
                            classNamePrefix='react-select'
                            className=''
                            isSearchable={true}
                            isClearable
                            name='icon'
                            options={DataIcons}
                            value={DataIcons?.find(f => f.value === formik.values.icon)}
                            onChange={option => formik.setFieldValue('icon', option?.value)}
                            placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
                          />
                          <>{formik.errors.icon}</>
                        </div>
                      </div>

                      <div className='col-xl-12'>
                        <div className='form-group mb-3'>
                          <label className='form-label'>{t('Note')}</label>
                          <Form.Control
                            className='form-control '
                            type='text'
                            autoComplete='off'
                            name='description'
                            onChange={formik.handleChange}
                            value={formik.values.description || ''}
                            isInvalid={!!(formik.touched.description && formik.errors.description)}
                          />
                          <Form.Control.Feedback type='invalid'>{formik.errors.description}</Form.Control.Feedback>
                        </div>
                      </div>
                    </div>

                    <div className='text-center'>
                      <button
                        hidden={!HasPermission('INVEditCategories')}
                        disabled={formik.isSubmitting}
                        type='submit'
                        className='btn me-1 btn-theme mb-1 w-100'
                      >
                        {formik.isSubmitting ? (
                          <div className='spinner-border spinner-border-sm me-2'></div>
                        ) : (
                          <i className='fa fa-save fa-fw me-1'></i>
                        )}
                        {t('Save')}
                      </button>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </>
        </Loading>
      </Card>
    </div>
  );
};

export default EditCategoryInv;
